// OXIDE UI skin — the ONE design language for every player-facing surface (OXIDE UI SYSTEM v0.1,
// owner 2026-07-13; spec archived at oxide-vault/resources/oxide-ui-system-v0.1.html + the vault page
// oxide-vault/design/ui-style-guide.md — if a value is not there, it does not ship).
//
// The look: warm near-black brown surfaces, one mono family, ORANGE = interactive/selected and
// NOTHING else, green = state-positive, red = destructive/insufficient, glass for in-game windows,
// opaque panels for menu screens. Import SK for tokens; injectSkinCss() once per entry for the
// shared classes; redHover()/rowHover() to wire hovers; chipCss()/kbd()/btnCss() for fragments.

export const SK = {
  // ---- base surfaces (darkest to lightest) ----
  bg0: '#0b0907',            // page void, wheel segment gaps
  bg1: '#14100c',            // app background (menu screens)
  surface1: '#1c1712',       // menu panels, list rows
  surface2: '#251f17',       // raised: inputs, chips, hover
  surface3: '#2e2820',       // highest: active segments, thumbs

  // ---- transparency ----
  scrim: 'rgba(8,6,4,0.55)',       // full-screen dim behind panels (death, map)
  glass: 'rgba(15,12,9,0.88)',     // in-game windows, wheel slices, tooltips
  glassLt: 'rgba(22,18,13,0.72)',  // hotbar, hint strips
  blur: '10px',                    // the ONE backdrop blur (0 in reduced-transparency mode)
  // legacy aliases (old call sites; sweep refines which precise token each surface uses)
  panel: 'rgba(15,12,9,0.88)',
  panelGlass: 'rgba(15,12,9,0.88)',
  well: 'rgba(0,0,0,0.28)',        // input/track inset
  raised: 'rgba(255,255,255,0.035)', // slot faces (a hair above the panel)

  // ---- borders ----
  border: '#332b21',
  borderSoft: 'rgba(255,255,255,0.07)', // glass panel edge
  borderStrong: '#4d4132',
  borderSel: '#e0562e',            // SELECTED outline = accent (the old tan is retired)

  // ---- text (4 steps, no more) ----
  textHi: '#f0e7d6',   // titles, item names, values
  text: '#cfc2ab',     // body
  dim: '#9a8c75',      // meta, costs, hints
  faint: '#6d6150',    // labels, disabled, placeholders

  // ---- accent: interaction and selection ONLY ----
  accent: '#e0562e',
  accentHi: '#f36f43',
  accentSoft: 'rgba(224,86,46,0.14)',
  accentGlow: 'rgba(224,86,46,0.38)',
  // legacy hover aliases (redHover once painted solid red; the language is now accent-soft)
  red: 'rgba(224,86,46,0.14)',
  redStroke: '#e0562e',

  // ---- semantic ----
  good: '#6fb35f', goodSoft: 'rgba(111,179,95,0.14)',   // live, researched, durability, confirm
  bad: '#e5484d', badSoft: 'rgba(229,72,77,0.12)',      // destructive, insufficient
  warn: '#d9a13c', warnSoft: 'rgba(217,161,60,0.14)',   // tier 3, cautions
  info: '#6f9fd8', infoSoft: 'rgba(111,159,216,0.14)',  // tier 2
  amber: '#d9a13c',   // legacy alias -> warn
  danger: '#e5484d',  // legacy alias -> bad

  // ---- vitals (fixed order: health, food, water) ----
  health: '#d2452b',
  food: '#e2d2b2',
  water: '#a9c9de',   // tinted blue so the two cream bars stop being twins

  white: '#ffffff',

  // ---- type ----
  // JetBrains Mono when the woff2 ships in the bundle; falls back to the system mono seamlessly.
  font: "'JetBrains Mono',ui-monospace,'Cascadia Mono',Menlo,Consolas,monospace",
  fsDisplay: '28px', fsTitle: '18px', fsHead: '15px', fsBody: '13px', fsSmall: '12px', fsMicro: '11px',
  lsDisplay: '0.22em', lsTitle: '0.10em', lsLabel: '0.16em',

  // ---- radius ----
  rSm: '6px', rMd: '10px', rLg: '14px', rPill: '999px',

  // ---- motion ----
  tFast: '120ms', tMed: '200ms', ease: 'cubic-bezier(.2,.7,.3,1)',
} as const;

// ---- css fragment helpers (inline-style call sites) -------------------------------------------

// In-world / reticle TEXT CHIP -- the one look for every floating label (hover pills, the [E]
// prompt, place/remove hints, the hammer aim chip). Glass + hairline border, mono. Returns css
// FRAGMENTS -- the caller adds its own positioning/display.
export function chipCss(size: 'sm' | 'md' | 'lg' = 'md'): string {
  const font = size === 'lg' ? '700 15px' : size === 'sm' ? '600 11.5px' : '600 12.5px';
  return [
    `font:${font} ${SK.font}`, `color:${SK.text}`,
    `background:${SK.glass}`, `border:1px solid ${SK.border}`, `border-radius:${SK.rSm}`,
    'padding:4px 11px', 'letter-spacing:0.5px', 'text-shadow:0 1px 2px rgba(0,0,0,0.8)',
  ].join(';');
}

// Key chip markup ("hotkeys always render as key chips, never bare letters"). Inline-safe HTML.
export function kbd(label: string): string {
  return `<span style="display:inline-block;padding:1px 7px;border:1px solid ${SK.borderStrong};border-bottom-width:2px;border-radius:5px;background:${SK.surface2};font:700 ${SK.fsMicro} ${SK.font};color:${SK.textHi};line-height:1.4">${label}</span>`;
}

// Section label fragment (FUEL / INPUT / OUTPUT / QUEUE ... always this style).
export function labelCss(): string {
  return `font:700 ${SK.fsMicro} ${SK.font};letter-spacing:${SK.lsLabel};text-transform:uppercase;color:${SK.faint}`;
}

// Window/screen title fragment (uppercase .ox-title).
export function titleCss(px = 18): string {
  return `font:700 ${px}px ${SK.font};letter-spacing:${SK.lsTitle};text-transform:uppercase;color:${SK.textHi}`;
}

// Button css fragments per the system's five kinds. Callers append sizing/positioning.
export function btnCss(kind: 'primary' | 'secondary' | 'ghost' | 'danger' | 'confirm' = 'secondary', small = false): string {
  const base = [
    'display:inline-flex', 'align-items:center', 'justify-content:center', 'gap:8px',
    `height:${small ? 28 : 36}px`, `padding:0 ${small ? 10 : 16}px`,
    `border-radius:${SK.rMd}`, 'border:1px solid transparent', 'background:transparent',
    `font:700 ${small ? SK.fsMicro : SK.fsSmall} ${SK.font}`, 'letter-spacing:0.04em',
    'cursor:pointer', 'user-select:none', `transition:all ${SK.tFast} ${SK.ease}`, `color:${SK.text}`,
  ];
  if (kind === 'primary') base.push(`background:${SK.accent}`, 'color:#fff6ec');
  else if (kind === 'secondary') base.push(`border-color:${SK.borderStrong}`);
  else if (kind === 'ghost') base.push(`color:${SK.dim}`);
  else if (kind === 'danger') base.push(`border-color:${SK.bad}`, `color:${SK.bad}`);
  else if (kind === 'confirm') base.push(`border-color:${SK.good}`, `color:${SK.good}`);
  return base.join(';');
}

// Panel fragments: 'menu' = opaque (no world behind), 'glass' = in-game window, 'glassLt' = hotbar/hints.
export function panelCss(kind: 'menu' | 'glass' | 'glassLt' = 'glass'): string {
  if (kind === 'menu') return `background:${SK.surface1};border:1px solid ${SK.border};border-radius:${SK.rLg}`;
  const bg = kind === 'glass' ? SK.glass : SK.glassLt;
  return `background:${bg};border:1px solid ${SK.borderSoft};border-radius:${SK.rLg};backdrop-filter:blur(${SK.blur});-webkit-backdrop-filter:blur(${SK.blur})`;
}

// ---- shared classes ----------------------------------------------------------------------------
// One injected stylesheet: css variables + the utility classes windows share. Idempotent.
let cssInjected = false;
export function injectSkinCss(): void {
  if (cssInjected || document.getElementById('ox-skin-style')) { cssInjected = true; return; }
  cssInjected = true;
  const s = document.createElement('style');
  s.id = 'ox-skin-style';
  // NOTE: the :root design tokens (--ox-*) live in tokens.css (the single source, imported at boot).
  // This stylesheet is only the shared UTILITY CLASSES that inline styles can't express.
  s.textContent = `
*{scrollbar-width:thin;scrollbar-color:${SK.surface3} transparent}
::-webkit-scrollbar{width:8px;height:8px}
::-webkit-scrollbar-thumb{background:${SK.surface3};border-radius:4px}
::-webkit-scrollbar-thumb:hover{background:${SK.borderStrong}}
::-webkit-scrollbar-track{background:transparent}
.ox-hover-row{transition:background ${SK.tFast} ${SK.ease},border-color ${SK.tFast} ${SK.ease}}
.ox-hover-row:hover{background:${SK.surface2}!important;border-color:${SK.borderStrong}!important}
.ox-kbd{display:inline-block;padding:1px 7px;border:1px solid ${SK.borderStrong};border-bottom-width:2px;border-radius:5px;background:${SK.surface2};font:700 ${SK.fsMicro} ${SK.font};color:${SK.textHi};line-height:1.4}
.ox-label{font:700 ${SK.fsMicro} ${SK.font};letter-spacing:${SK.lsLabel};text-transform:uppercase;color:${SK.faint}}
.ox-badge{display:inline-flex;align-items:center;gap:6px;padding:3px 9px;border-radius:${SK.rPill};font:700 10px ${SK.font};letter-spacing:0.12em;text-transform:uppercase;border:1px solid transparent}
.ox-badge--live{color:${SK.good};background:${SK.goodSoft}}
.ox-badge--t1{color:${SK.good};background:${SK.goodSoft}}
.ox-badge--t2{color:${SK.info};background:${SK.infoSoft}}
.ox-badge--t3{color:${SK.warn};background:${SK.warnSoft}}
.ox-badge--count{color:${SK.textHi};background:${SK.surface2};border-color:${SK.border}}
@media (prefers-reduced-motion: reduce){.ox-hover-row{transition:none}}
`;
  document.head.appendChild(s);
}

// Hover for BUTTON-LIKE things and wheel-adjacent rows: accent-soft fill + accent border + lifted
// text (orange = interactive). Restores whatever inline styles the element had on the way out.
export function redHover(el: HTMLElement): void {
  let bg = '', col = '', bd = '';
  el.addEventListener('mouseenter', () => {
    bg = el.style.background; col = el.style.color; bd = el.style.borderColor;
    el.style.background = SK.accentSoft;
    el.style.color = SK.textHi;
    el.style.borderColor = SK.accent;
  });
  el.addEventListener('mouseleave', () => {
    el.style.background = bg;
    el.style.color = col;
    el.style.borderColor = bd;
  });
}

// Hover for LIST ROWS (server rows, recipe rows, storage rows): neutral raise, no accent -- the
// accent is reserved for the selected row. Restores inline styles on leave.
export function rowHover(el: HTMLElement): void {
  let bg = '', bd = '';
  el.addEventListener('mouseenter', () => {
    bg = el.style.background; bd = el.style.borderColor;
    el.style.background = SK.surface2;
    el.style.borderColor = SK.borderStrong;
  });
  el.addEventListener('mouseleave', () => {
    el.style.background = bg;
    el.style.borderColor = bd;
  });
}
